import { useCallback, useEffect, useMemo, useState } from 'react'

import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'

import ToggleInput from '~/components/Toggle'

import AlertBrands from '../../../../AlertBrands'
import { formatDestinationUrls } from '../../helpers/formatDestinationUrls'
import { formatReviewDestinationUrls } from '../../helpers/formatReviewDestinationUrls'
import { getCurrentDestinationValues } from '../../helpers/getCurrentDestinationValues'
import { getDestinationName } from '../../helpers/getDestinationName'
import { getDestinationUrlsState } from '../../helpers/getDestinationUrlsState'
import DestinationUrlsContent, {
  SubpublisherDestination
} from '../DestinationUrlsContent'
import EditFormDestinationURL from '../EditForm'
import DestinationUrlsFields, { DestinationUrlValue } from '../Fields'
import OwnApprovedDestinationUrlsEditor from '../OwnApprovedDestinationUrlsEditor'

const DestinationUrlsBody = ({
  data,
  isMultiBrand,
  destinationsConfig,
  catalogLoaded = false,
  subpublishers,
  effectiveEditMode,
  isDestinationUrlEnabled,
  onDestinationUrlEnabledChange,
  pendingValue,
  onPendingValueChange,
  onSubpublisherSaved,
  onCancelEdit
}: {
  data: CampaignView
  isMultiBrand: boolean
  destinationsConfig?: DestinationUrlsConfig[]
  catalogLoaded?: boolean
  subpublishers: SubpublisherDestination[]
  effectiveEditMode: boolean
  isDestinationUrlEnabled: boolean
  onDestinationUrlEnabledChange?: (value: boolean) => void
  pendingValue?: DestinationUrlValue[] | null
  onPendingValueChange?: (urls: DestinationUrlValue[]) => void
  onSubpublisherSaved: () => void
  onCancelEdit: () => void
}) => {
  const [groupBrands, setGroupBrands] = useState(false)

  const {
    publisherId,
    isSubpublisherReview,
    isRejectedSubpublisherReview,
    showSubpublisherSections,
    pendingReviewMode,
    awaitingDistribution,
    destinationUrl,
    destinationUrls,
    isSingleDestination
  } = getDestinationUrlsState({
    data,
    isMultiBrand,
    destinationCount: destinationsConfig?.length
  })

  const hasProjectedBrands = subpublishers.some(subpublisher =>
    subpublisher.destinationUrls.some(destination => !!destination.brand)
  )
  const effectiveGroupBrands = hasProjectedBrands && groupBrands

  const getName = useCallback(
    (publisherId: string, brand: string) =>
      getDestinationName(destinationsConfig, publisherId, brand),
    [destinationsConfig]
  )

  const formattedDestinationUrls = useMemo(
    () =>
      formatDestinationUrls({
        isSubpublisherReview,
        pendingReviewMode,
        savedDestinationUrls: data.raw?.destination_urls,
        destinationsConfig,
        catalogLoaded,
        isSingleDestination,
        destinationUrl,
        destinationUrls
      }),
    [
      data.raw?.destination_urls,
      destinationUrl,
      destinationUrls,
      destinationsConfig,
      catalogLoaded,
      isSingleDestination,
      isSubpublisherReview,
      pendingReviewMode
    ]
  )

  const currentDestinationValues = useMemo(
    () =>
      getCurrentDestinationValues({
        pendingReviewMode,
        pendingValue,
        isSingleDestination,
        destinationUrl,
        destinationUrls,
        destinationsConfig,
        catalogLoaded
      }),
    [
      destinationUrl,
      destinationUrls,
      destinationsConfig,
      catalogLoaded,
      isSingleDestination,
      pendingReviewMode,
      pendingValue
    ]
  )

  // Until the reviewer types, pending is null and the approve modal would
  // send the raw list, including rows this panel is not showing. Seed it
  // with the list on screen. A later edit replaces this value.
  useEffect(() => {
    if (!pendingReviewMode || pendingValue != null) return

    onPendingValueChange?.(currentDestinationValues)
  }, [
    currentDestinationValues,
    onPendingValueChange,
    pendingReviewMode,
    pendingValue
  ])

  const expectedDestinationCount = isSingleDestination
    ? 1
    : destinationsConfig?.length || 0

  const filledDestinationCount = formatReviewDestinationUrls(
    currentDestinationValues
  ).length

  // Leaving some brands out is a valid decision, so the warning only helps
  // when nothing at all was filled in.
  const hasNoDestinationUrls =
    isDestinationUrlEnabled &&
    expectedDestinationCount > 0 &&
    filledDestinationCount === 0

  const showDestinationUrlWarning = pendingReviewMode && hasNoDestinationUrls

  const showRejectSuggestedUrlsWarning =
    pendingReviewMode &&
    !isDestinationUrlEnabled &&
    formatReviewDestinationUrls(data.raw?.destination_urls).length > 0

  // Once approved, a sub-publisher edits through the pencil icon like the
  // advertiser does; on the per-sub-publisher view only the network edits.
  const showEditForm =
    !showSubpublisherSections && !pendingReviewMode && effectiveEditMode

  return (
    <>
      {isMultiBrand && !showSubpublisherSections && (
        <header className="mb-3">
          <AlertBrands publisherId={publisherId} />
        </header>
      )}

      {pendingReviewMode && (
        <div className="mb-1">
          <ToggleInput
            name="destination_url_enabled"
            label={t('rm:reviewCampaign.destinationUrl.toggle')}
            isChecked={isDestinationUrlEnabled}
            textLeft
            size="small"
            onChange={({ e }: ToogleOnChangeProps) =>
              onDestinationUrlEnabledChange?.(e.target.checked)
            }
          />
          {showRejectSuggestedUrlsWarning && (
            <Alert variant="warning" style={{ marginTop: '0.75rem' }}>
              {t('rm:reviewCampaign.destinationUrl.toggleOff.rejectWarning')}
            </Alert>
          )}
        </div>
      )}

      {/* Modo pendente de revisão (sub-publisher): campos controlados,
          sincronizados com a modal de aprovação via estado do pai. Ocultos
          quando o switch "URL de destino" está desligado — os valores
          digitados continuam preservados no estado do componente pai
          (pendingValue), só não são exibidos nem exigidos. */}
      {pendingReviewMode && isDestinationUrlEnabled && (
        <div className="d-flex flex-column gap-3">
          <DestinationUrlsFields
            value={currentDestinationValues}
            onChange={onPendingValueChange}
            destinationsConfig={destinationsConfig}
            isMultiDestination={!isSingleDestination}
            isRequired={false}
          />
          {showDestinationUrlWarning && (
            <Alert variant="warning">{t('rm:addDestinationUrls')}</Alert>
          )}
        </div>
      )}

      {isRejectedSubpublisherReview && (
        <Alert variant="warning" style={{ marginBottom: '1rem' }}>
          {t('rm:destinationUrls.rejectedEditDisabled')}
        </Alert>
      )}

      {showSubpublisherSections && hasProjectedBrands && (
        <div className="mb-3">
          <ToggleInput
            name="group_destination_brands"
            label={t('rm:destinationUrls.groupBrands')}
            isChecked={groupBrands}
            textLeft
            size="small"
            onChange={({ e }: ToogleOnChangeProps) =>
              setGroupBrands(e.target.checked)
            }
          />
        </div>
      )}

      {/* Modo visualização */}
      {!pendingReviewMode && !effectiveEditMode && (
        <section className="d-flex flex-column gap-3">
          <DestinationUrlsContent
            campaignId={data.id}
            showSubpublisherSections={showSubpublisherSections}
            subpublishers={subpublishers}
            groupBrands={effectiveGroupBrands}
            awaitingDistribution={awaitingDistribution}
            formattedDestinationUrls={formattedDestinationUrls}
            getName={getName}
            onSubpublisherSaved={onSubpublisherSaved}
          />
        </section>
      )}

      {/* Modo edição (pós-aprovação) — só no fluxo legado / revisão do
          sub-publisher. Na visão por sub-publisher, a edição é inline
          (lápis) e só a rede pode editar. */}
      {showEditForm && (
        <>
          {isSubpublisherReview ? (
            <OwnApprovedDestinationUrlsEditor
              data={data}
              destinationsConfig={destinationsConfig || []}
              onCancel={onCancelEdit}
            />
          ) : (
            <EditFormDestinationURL
              data={data}
              onDisableEditMode={onCancelEdit}
              destinationsConfig={destinationsConfig || []}
              isMultiDestination={!isSingleDestination}
            />
          )}
        </>
      )}
    </>
  )
}

export default DestinationUrlsBody
